import {
  IRynReplayEnvelope,
  RYN_REPLAY_APP_EVENT_TYPE,
  RYN_REPLAY_CONTEXT_EVENT_TYPE,
  RYN_REPLAY_IDENTITY_EVENT_TYPE,
  RYN_REPLAY_VIEWPORT_EVENT_TYPE,
} from '@ups/xplat/web/core';
import {
  IRynReplayMobileTouchPayload,
  IRynReplayMobileScreenshot,
  readRynReplayMobileScreenshot,
  IRynReplayViewNode,
  RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE,
  RYN_REPLAY_MOBILE_FRAME_EVENT_TYPE,
  RYN_REPLAY_MOBILE_TOUCH_EVENT_TYPE,
} from '@ups/xplat/base/ryn-replay';
import {
  IRynReplayConsoleEntry,
  IRynReplayNetworkEntry,
  IRynReplayPerformanceEntry,
  RYN_REPLAY_CONSOLE_EVENT_TYPE,
  RYN_REPLAY_NETWORK_EVENT_TYPE,
  RYN_REPLAY_PERFORMANCE_EVENT_TYPE,
  readRynReplayConsoleEntry,
  readRynReplayNetworkEntry,
  readRynReplayPerformanceEntry,
} from './ryn-replay-devtools';
import {
  IRynReplayIdentitySnapshot,
  IRynReplaySessionAttribution,
  recordEnvelopeAttributionOnStream,
  recordRynReplayIdentityOnStream,
} from './ryn-replay-identity';
import {
  IRynReplayPageTrailEntry,
  mergeRynReplayPageTrails,
  recordRynReplayPageTrail,
} from './ryn-replay-page-trail';
import {
  readClientContext,
  readViewportMarker,
} from './ryn-replay-stream-readers';

/**
 * Turns the read API's NDJSON stream back into rrweb events (#30072).
 *
 * Kept pure and DOM-free on purpose. This is the point where a recording is
 * either recovered or lost, and none of that should need a browser to test.
 *
 * Why a bad line is skipped instead of thrown:
 *
 * The last batch of a session is the one most likely to be damaged. It gets
 * written by a pagehide flush racing a closing tab, and a retention sweep can
 * delete a blob in between the read pipeline listing it and opening it.
 *
 * If we threw on a bad line, "the last two seconds are missing" would become
 * "this recording will not open". That is a terrible trade for a support
 * engineer who has exactly one recording of the bug they are chasing.
 *
 * So damage becomes a count the player can show, and everything up to the
 * damage still plays. `malformedLines` is what lets the player say the
 * recording is partial, rather than quietly showing a short one and letting
 * someone conclude the user stopped doing anything.
 */

/** One `track()` marker, lifted out for a playback timeline. */
export interface IRynReplayAppEventMarker {
  name: string;
  /** Epoch milliseconds UTC, matching the rrweb event clock. */
  ts: number;
  url?: string;
  /**
   * Property bag from `track(name, properties)`. string|number only — nested
   * payloads (e.g. Equipment Usage `rowsJson`) ride as JSON strings (#31398).
   */
  properties?: Record<string, string | number>;
}

/**
 * One Viewport / resize / zoom marker from the recorder (#30070).
 *
 * Payload is the snapshot itself (not wrapped under `event`), stamped at
 * capture time. The player applies these during playback so mid-session
 * resizes and browser zoom match what the user saw.
 */
export interface IRynReplayViewportMarker {
  /** Epoch milliseconds UTC, matching the rrweb event clock. */
  ts: number;
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  visualViewportScale?: number;
}

/**
 * Client context snapshot from the session-start `Context` event (#30211).
 * Absent on sessions recorded before context capture landed — normal, not damage.
 */
export interface IRynReplayClientContext {
  platform?: string;
  userAgent?: string;
  deviceModel?: string;
  sdkVersion?: string;
  language?: string;
  timeZone?: string;
  screenWidth?: number;
  screenHeight?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  appVersion?: string;
  buildNumber?: string;
  environment?: string;
}

/**
 * What the stream itself said about its own wholeness.
 *
 * `unconfirmed` is the state that made the trailer necessary. A body that simply
 * stops looks byte-for-byte like a body that finished, so before the trailer
 * existed a connection dropped mid-write was indistinguishable from a clean
 * read, and both rendered as a user who went quiet. This value records which of
 * the two we can actually tell.
 */
export type RynReplayCompletenessStatus =
  'complete' | 'truncated' | 'unconfirmed';

export interface IRynReplayCompleteness {
  status: RynReplayCompletenessStatus;
  /** Segment counts off the trailer. Absent when no trailer was seen. */
  batchesWritten?: number;
  batchesExpected?: number;
  batchesSkipped?: number;
}

export interface IRynReplayStream {
  /**
   * rrweb events in playback order, ready to hand to rrweb-player. Typed as
   * `unknown[]` on purpose: this layer deliberately does not import rrweb's
   * types, so a rrweb major version cannot break the parser, and nothing here
   * inspects an event beyond confirming it is shaped like one.
   */
  events: unknown[];
  /** `track()` markers, for a timeline the vendor products charge extra for. */
  appEvents: IRynReplayAppEventMarker[];
  /**
   * Viewport / zoom markers in capture order. Applied by the player during
   * playback; never handed to rrweb (they are not rrweb events).
   */
  viewports: IRynReplayViewportMarker[];
  /** Page url changes off the envelope `url`, for page-scoped views (#31329). */
  pageTrail: IRynReplayPageTrailEntry[];
  /**
   * The captured console stream, for the devtools panel (#30095). Empty on
   * sessions recorded before the capture side landed (#30070), which the panel
   * treats as "nothing captured", not as damage.
   */
  consoleEvents: IRynReplayConsoleEntry[];
  /** Captured network requests, same provenance and same caveat as above. */
  networkEvents: IRynReplayNetworkEntry[];
  /**
   * Captured Core Web Vitals / paint timing (#32052). Empty on sessions
   * recorded before performance capture shipped.
   */
  performanceEvents: IRynReplayPerformanceEntry[];
  /**
   * Schematic frames from a mobile recorder, in capture order. Rendered by the
   * mobile replayer, never handed to rrweb. Empty on every web session.
   */
  mobileFrames: IRynReplayMobileFrame[];
  /** Touches from a mobile recorder, in capture order. Empty on web sessions. */
  mobileTouches: IRynReplayMobileTouch[];
  /** Lines that were not JSON at all. Non-zero means the recording is partial. */
  malformedLines: number;
  /**
   * Envelopes that parsed but carried no rrweb event: identity records, app
   * events, and anything a future recorder adds. Expected to be non-zero on
   * every real session, so it is NOT a damage signal.
   */
  nonReplayEnvelopes: number;
  /** True when the session carried an identity record at any point. */
  hasIdentity: boolean;
  /** Identity envelopes in capture order. Empty when nobody identified. */
  identities: IRynReplayIdentitySnapshot[];
  /** App that recorded the session (first envelope `app`), never the player's host. */
  app?: string;
  /** First app version label seen on an envelope (may be absent on old sessions). */
  appVersion?: string;
  /** First build number seen on an envelope (may be absent on old sessions). */
  buildNumber?: string;
  /** First Context snapshot (#30211); absent on older sessions. */
  context?: IRynReplayClientContext;
  /**
   * Session index attribution from the read API response headers when the
   * stream itself carried no Identity envelopes.
   */
  sessionAttribution?: IRynReplaySessionAttribution;
  /**
   * The read surface's own verdict on whether every listed segment reached us,
   * taken from the `__replay` trailer line rather than from the status code. The
   * server commits its 200 and all three `X-Replay-*` headers before the first
   * batch is opened, so the status is a value chosen before the work happened.
   */
  completeness: IRynReplayCompleteness;
}

/** One mobile view tree, lifted out of its envelope for the mobile replayer. */
export interface IRynReplayMobileFrame {
  screenshot?: IRynReplayMobileScreenshot;
  /** Epoch milliseconds UTC, off the envelope. */
  ts: number;
  root: IRynReplayViewNode;
  /** True when sent as a keyframe (navigation, dialog, resume). */
  keyframe: boolean;
  /** True when the recorder hit its depth or node ceiling on this tree. */
  truncated: boolean;
  /** Route the screen was on, off the envelope. */
  url?: string;
}

/** One mobile touch, lifted out of its envelope for the mobile replayer. */
export interface IRynReplayMobileTouch extends IRynReplayMobileTouchPayload {
  /** Epoch milliseconds UTC, off the envelope. */
  ts: number;
}

const REPLAY_TRAILER_KEY = '__replay';

const tryReadStreamVersionLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 50) {
    return undefined;
  }
  return trimmed;
};

const stampStreamVersionFromEnvelope = (
  stream: IRynReplayStream,
  envelope: IRynReplayEnvelope
): void => {
  stream.app ??= tryReadStreamVersionLabel(envelope.app);
  stream.appVersion ??= tryReadStreamVersionLabel(envelope.appVersion);
  stream.buildNumber ??= tryReadStreamVersionLabel(envelope.buildNumber);

  const ua =
    typeof envelope.userAgent === 'string'
      ? envelope.userAgent.trim() || undefined
      : undefined;
  const env =
    typeof envelope.environment === 'string'
      ? tryReadStreamVersionLabel(envelope.environment)
      : undefined;
  if (!ua && !env) {
    return;
  }
  stream.context = {
    ...(stream.context ?? {}),
    ...(ua && !stream.context?.userAgent ? { userAgent: ua } : {}),
    ...(env && !stream.context?.environment ? { environment: env } : {}),
  };
};

/**
 * An rrweb event, minimally. The numeric `type` is the discriminator rrweb
 * itself uses, and checking it is what keeps an identity or app-event envelope
 * from reaching the player, where a missing `type` throws inside rrweb's own
 * dispatch rather than anywhere we could report it.
 */
const isRrwebEvent = (candidate: unknown): boolean =>
  typeof candidate === 'object' &&
  candidate !== null &&
  typeof (candidate as { type?: unknown }).type === 'number';

const readTrailer = (
  candidate: Record<string, unknown>
): IRynReplayCompleteness | undefined => {
  const status = candidate[REPLAY_TRAILER_KEY];
  if (status !== 'complete' && status !== 'truncated') {
    // A trailer carrying an unknown status is a producer newer than this
    // parser. Deliberately NOT read as complete: the one thing this record
    // exists to prevent is claiming wholeness we were not told about.
    return undefined;
  }
  const count = (key: string): number | undefined =>
    typeof candidate[key] === 'number' ? (candidate[key] as number) : undefined;
  return {
    status,
    batchesWritten: count('batchesWritten'),
    batchesExpected: count('batchesExpected'),
    batchesSkipped: count('batchesSkipped'),
  };
};

const readAppEventProperties = (
  raw: unknown
): Record<string, string | number> | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const out: Record<string, string | number> = {};
  let any = false;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string' || typeof value === 'number') {
      out[key] = value;
      any = true;
    }
  }
  return any ? out : undefined;
};

const readAppEventMarker = (
  envelope: IRynReplayEnvelope
): IRynReplayAppEventMarker | undefined => {
  const payload = envelope.payload as {
    name?: unknown;
    properties?: unknown;
  };
  const name = payload?.name;
  if (typeof name !== 'string' || name.length === 0) {
    return undefined;
  }
  const properties = readAppEventProperties(payload?.properties);
  return {
    name,
    ts: envelope.ts,
    url: envelope.url,
    ...(properties ? { properties } : {}),
  };
};

/**
 * Parse one NDJSON body from `GET /api/sessions/{id}/events`.
 *
 * Order is PRESERVED, never re-sorted. The read pipeline concatenates batches in
 * playback order and the recorder stamps rrweb's own timestamps, so sorting here
 * would only ever paper over an ordering bug on the server, where it is real and
 * fixable, by hiding it in the client, where it is not.
 */
export const parseRynReplayStream = (body: string): IRynReplayStream => {
  const stream = createEmptyRynReplayStream();

  for (const rawLine of body.split('\n')) {
    appendRynReplayNdjsonLine(stream, rawLine);
  }

  return stream;
};

/** Empty stream used as the accumulator for progressive / paginated loads. */
export const createEmptyRynReplayStream = (): IRynReplayStream => ({
  events: [],
  appEvents: [],
  consoleEvents: [],
  networkEvents: [],
  performanceEvents: [],
  mobileFrames: [],
  mobileTouches: [],
  viewports: [],
  pageTrail: [],
  malformedLines: 0,
  nonReplayEnvelopes: 0,
  hasIdentity: false,
  identities: [],
  completeness: { status: 'unconfirmed' },
});

const mergeRynReplayCompleteness = (
  left: IRynReplayCompleteness,
  right: IRynReplayCompleteness
): IRynReplayCompleteness => {
  if (left.status === 'truncated' || right.status === 'truncated') {
    return right.status === 'truncated' ? right : left;
  }
  if (left.status === 'unconfirmed' || right.status === 'unconfirmed') {
    return { status: 'unconfirmed' };
  }
  return right;
};

/** Concatenate two parsed bodies in playback order (paginated fetch chunks). */
export const mergeRynReplayStreams = (
  left: IRynReplayStream,
  right: IRynReplayStream
): IRynReplayStream => {
  const context =
    left.context || right.context
      ? { ...(left.context ?? {}), ...(right.context ?? {}) }
      : undefined;

  return {
    events: left.events.concat(right.events),
    appEvents: left.appEvents.concat(right.appEvents),
    viewports: left.viewports.concat(right.viewports),
    pageTrail: mergeRynReplayPageTrails(left.pageTrail, right.pageTrail),
    consoleEvents: left.consoleEvents.concat(right.consoleEvents),
    networkEvents: left.networkEvents.concat(right.networkEvents),
    performanceEvents: left.performanceEvents.concat(right.performanceEvents),
    mobileFrames: left.mobileFrames.concat(right.mobileFrames),
    mobileTouches: left.mobileTouches.concat(right.mobileTouches),
    malformedLines: left.malformedLines + right.malformedLines,
    nonReplayEnvelopes: left.nonReplayEnvelopes + right.nonReplayEnvelopes,
    hasIdentity: left.hasIdentity || right.hasIdentity,
    identities: left.identities.concat(right.identities),
    app: left.app ?? right.app,
    appVersion: left.appVersion ?? right.appVersion,
    buildNumber: left.buildNumber ?? right.buildNumber,
    context,
    sessionAttribution: left.sessionAttribution ?? right.sessionAttribution,
    completeness: mergeRynReplayCompleteness(
      left.completeness,
      right.completeness
    ),
  };
};

const appendRynReplayNdjsonLine = (
  stream: IRynReplayStream,
  rawLine: string
): void => {
  const line = rawLine.trim();
  if (line.length === 0) {
    return;
  }

  let envelope: IRynReplayEnvelope;
  try {
    envelope = JSON.parse(line) as IRynReplayEnvelope;
  } catch {
    stream.malformedLines += 1;
    return;
  }

  if (
    envelope !== null &&
    typeof envelope === 'object' &&
    REPLAY_TRAILER_KEY in envelope
  ) {
    const trailer = readTrailer(envelope as unknown as Record<string, unknown>);
    if (trailer && stream.completeness.status !== 'truncated') {
      stream.completeness = trailer;
    }
    return;
  }

  stampStreamVersionFromEnvelope(stream, envelope);
  recordRynReplayPageTrail(stream.pageTrail, envelope);

  if (envelope?.type === RYN_REPLAY_IDENTITY_EVENT_TYPE) {
    recordRynReplayIdentityOnStream(stream, envelope);
    stream.nonReplayEnvelopes += 1;
    return;
  }

  recordEnvelopeAttributionOnStream(stream, envelope);

  if (envelope?.type === RYN_REPLAY_APP_EVENT_TYPE) {
    const marker = readAppEventMarker(envelope);
    if (marker) {
      stream.appEvents.push(marker);
    }
    stream.nonReplayEnvelopes += 1;
    return;
  }

  if (envelope?.type === RYN_REPLAY_CONSOLE_EVENT_TYPE) {
    const entry = readRynReplayConsoleEntry(envelope);
    if (entry) {
      stream.consoleEvents.push(entry);
    }
    stream.nonReplayEnvelopes += 1;
    return;
  }

  if (envelope?.type === RYN_REPLAY_NETWORK_EVENT_TYPE) {
    const entry = readRynReplayNetworkEntry(envelope);
    if (entry) {
      stream.networkEvents.push(entry);
    }
    stream.nonReplayEnvelopes += 1;
    return;
  }

  if (envelope?.type === RYN_REPLAY_PERFORMANCE_EVENT_TYPE) {
    const entry = readRynReplayPerformanceEntry(envelope);
    if (entry) {
      stream.performanceEvents.push(entry);
    }
    stream.nonReplayEnvelopes += 1;
    return;
  }

  if (envelope?.type === RYN_REPLAY_VIEWPORT_EVENT_TYPE) {
    const marker = readViewportMarker(envelope);
    if (marker) {
      stream.viewports.push(marker);
    }
    stream.nonReplayEnvelopes += 1;
    return;
  }

  if (envelope?.type === RYN_REPLAY_CONTEXT_EVENT_TYPE) {
    const context = readClientContext(envelope);
    if (context) {
      stream.context = { ...(stream.context ?? {}), ...context };
    }
    stream.nonReplayEnvelopes += 1;
    return;
  }

  const inner = (envelope?.payload as { event?: unknown })?.event;
  if (isRrwebEvent(inner)) {
    stream.events.push(inner);
    return;
  }

  // After the rrweb check, not before: a web FullSnapshot and a mobile keyframe
  // share the `FullSnapshot` token, and only the payload tells them apart.
  const frame = readMobileFrame(envelope);
  if (frame) {
    stream.mobileFrames.push(frame);
    return;
  }

  if (envelope?.type === RYN_REPLAY_MOBILE_TOUCH_EVENT_TYPE) {
    const touch = readMobileTouch(envelope);
    if (touch) {
      stream.mobileTouches.push(touch);
      return;
    }
  }

  stream.nonReplayEnvelopes += 1;
};

/**
 * Only the root is checked here. A tree can hold 1,500 nodes and a session
 * hundreds of frames, so a full walk at parse time is the expensive way to
 * learn what the renderer finds out anyway — it skips malformed nodes as it
 * draws them.
 */
const readMobileFrame = (
  envelope: IRynReplayEnvelope
): IRynReplayMobileFrame | undefined => {
  if (
    envelope?.type !== RYN_REPLAY_MOBILE_FRAME_EVENT_TYPE &&
    envelope?.type !== RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE
  ) {
    return undefined;
  }
  const payload = envelope.payload as Record<string, unknown> | undefined;
  const root = payload?.['root'] as IRynReplayViewNode | undefined;
  if (
    typeof payload?.['v'] !== 'number' ||
    !root ||
    typeof root !== 'object' ||
    typeof root.type !== 'string' ||
    !isFrameRect(root.frame)
  ) {
    return undefined;
  }
  return {
    ts: envelope.ts,
    root,
    keyframe: envelope.type === RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE,
    truncated: payload['truncated'] === true,
    screenshot: readRynReplayMobileScreenshot(payload['screenshot']),
    ...(typeof envelope.url === 'string' && envelope.url
      ? { url: envelope.url }
      : {}),
  };
};

const readMobileTouch = (
  envelope: IRynReplayEnvelope
): IRynReplayMobileTouch | undefined => {
  const payload = envelope.payload as Record<string, unknown> | undefined;
  const x = payload?.['x'];
  const y = payload?.['y'];
  const kind = payload?.['kind'];
  if (
    typeof kind !== 'string' ||
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    !Number.isFinite(x) ||
    !Number.isFinite(y)
  ) {
    return undefined;
  }
  return {
    ...(payload as unknown as IRynReplayMobileTouchPayload),
    ts: envelope.ts,
    kind,
    x,
    y,
  };
};

const isFrameRect = (candidate: unknown): boolean => {
  const rect = candidate as Record<string, unknown> | undefined;
  return (
    !!rect &&
    ['x', 'y', 'w', 'h'].every(
      (key) =>
        typeof rect[key] === 'number' && Number.isFinite(rect[key] as number)
    )
  );
};

/** True for a session a mobile recorder wrote. */
export const isRynReplayMobileStream = (stream: IRynReplayStream): boolean =>
  stream.mobileFrames.length > 0;

/**
 * Everything a replayer plays back, whichever recorder wrote it. The
 * progressive loader uses this to decide whether a fetched page added anything;
 * counting rrweb events alone reads every page of a mobile session as empty.
 */
export const rynReplayPlaybackItemCount = (stream: IRynReplayStream): number =>
  stream.events.length +
  stream.mobileFrames.length +
  stream.mobileTouches.length;

/**
 * rrweb-player refuses to mount on fewer than two events, and a stream with no
 * FullSnapshot has nothing to render deltas against. Both produce an opaque
 * failure inside rrweb, so the player checks here and says which one happened.
 *
 * `2` is rrweb's `EventType.FullSnapshot`, matched to the recorder's own
 * `RYN_REPLAY_EVENT_TYPE_NAMES` table rather than imported, for the same reason
 * that table exists: a rrweb major version must not silently renumber it.
 */
export const RRWEB_FULL_SNAPSHOT_TYPE = 2;

export const describeRynReplayPlayability = (
  stream: IRynReplayStream
): string | undefined => {
  // A single mobile frame is a whole screen, so there is no keyframe to be
  // missing and no minimum length — unlike rrweb, which replays deltas.
  if (isRynReplayMobileStream(stream)) {
    return undefined;
  }
  if (stream.events.length === 0) {
    // #30070 AC wording for a session that exists but carries no replayable
    // events (e.g. 200 with an empty body after retention aged the blobs out).
    return 'No data for this session.';
  }
  if (stream.events.length < 2) {
    return 'This recording is too short to play back.';
  }
  const hasSnapshot = stream.events.some(
    (event) => (event as { type?: number }).type === RRWEB_FULL_SNAPSHOT_TYPE
  );
  if (!hasSnapshot) {
    return 'This recording is missing its opening snapshot, so it cannot be rendered.';
  }
  return undefined;
};

/**
 * One sentence about how whole the recording is, or nothing when it is whole.
 *
 * Separate from {@link describeRynReplayPlayability} because the two answer
 * opposite questions. Playability decides whether to mount at all. This decides
 * what to say ALONGSIDE a recording that does play, and neither of its two
 * signals is a reason to withhold playback: a session whose tail was lost still
 * answers most of the questions someone opened it to ask.
 *
 * Both signals are reported together on purpose. They have different causes
 * (a damaged line inside a segment we did read, versus a segment that never
 * reached us at all) and a recording can carry both, so surfacing only the first
 * one found would hide real loss behind other real loss.
 *
 * WHY `unconfirmed` IS DELIBERATELY SILENT. An absent trailer has two causes
 * that cannot be told apart from the body alone: the read died mid-write, or the
 * producer predates the trailer (an older deployed read API, or a `.ndjson`
 * export taken before it existed). Warning on it today would fire on every
 * legacy export, and a warning that fires on healthy input is how the only
 * completeness signal we have gets trained away. Promote it to a visible notice
 * once every read surface that can serve this player is known to emit a trailer.
 */
export const describeRynReplayIntegrity = (
  stream: IRynReplayStream
): string | undefined => {
  const notices: string[] = [];

  if (stream.malformedLines > 0) {
    notices.push(
      `This recording is incomplete: ${stream.malformedLines} damaged segment(s) could not be read, most likely the final moments of the session.`
    );
  }

  if (stream.completeness.status === 'truncated') {
    const { batchesWritten, batchesExpected } = stream.completeness;
    // Trailer counts are scoped to the page that reported truncated (one
    // fromBatch/toBatch response), not necessarily the whole session — so
    // phrase them as a delivered range, not as session progress.
    const counted =
      typeof batchesWritten === 'number' && typeof batchesExpected === 'number'
        ? ` ${batchesWritten} of ${batchesExpected} segment(s) were delivered in the truncated range.`
        : '';
    notices.push(
      `The replay service reported this recording as truncated, so playback stops earlier than the session did.${counted}`
    );
  }

  return notices.length > 0 ? notices.join(' ') : undefined;
};
